import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import MapView from "./MapView";
import IncidentFeed from "./IncidentFeed";
import IncidentDetail from "./IncidentDetail";
import StationsBoard from "./StationsBoard";
import ApplianceBoard from "./ApplianceBoard";
import { api } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const DECODER = [
  ["P", "Pumper"],
  ["PT", "Pumper Tanker"],
  ["TB", "Teleboom"],
  ["WT", "Water Tanker"],
  ["LP", "Ladder Platform"],
  ["UP", "Ultra Large Pumper"],
  ["HZ", "HAZMAT"],
  ["R", "Rescue"],
  ["T", "Tanker"],
  ["S", "Slip-on"],
  ["BA", "Breathing Apparatus"],
  ["SV", "Salvage"],
  ["CU", "Control Unit"],
  ["SU", "Support"],
];

export default function LiveDemo({ user }) {
  const [incidents, setIncidents] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [stations, setStations] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [feedFilter, setFeedFilter] = useState("ALL");
  const [feedSearch, setFeedSearch] = useState("");
  const [viewMode, setViewMode] = useState("feed");
  const seenRef = useRef(new Set());
  const firstLoadDone = useRef(false);
  const matchRef = useRef({ inArea: () => false, isHome: () => false, isWatched: () => false });

  const visible =
    feedFilter === "ALL"
      ? incidents
      : incidents.filter((i) => i.agency === feedFilter);
  const agencyCount = (a) => incidents.filter((i) => i.agency === a).length;

  const profile = user?.profile || {};
  const alertSuburb = (profile.suburb || "").trim().toUpperCase();
  const homeSuburb = (() => {
    const name = profile.home_station_name || "";
    if (!name) return "";
    if (name.includes("·"))
      return name.split("·")[1].replace(/\d{4}/, "").trim().toUpperCase();
    return name.replace(/Fire Brigade/i, "").trim().toUpperCase();
  })();

  const inArea = (inc) =>
    alertSuburb &&
    ((inc.locality || "").toUpperCase().includes(alertSuburb) ||
      (inc.address || "").toUpperCase().includes(alertSuburb));
  const isHome = (inc) => {
    if (!profile.home_station_name) return false;
    if (homeSuburb && (inc.locality || "").toUpperCase().includes(homeSuburb))
      return true;
    return (inc.appliances || []).some(
      (a) => profile.home_station_code && a.callsign.startsWith(profile.home_station_code)
    );
  };

  const homeIds = new Set(visible.filter(isHome).map((i) => i.id));
  const areaIds = new Set(
    visible.filter((i) => !homeIds.has(i.id) && inArea(i)).map((i) => i.id)
  );
  const watchAreas = profile.watch_areas || [];
  const watchSuburbs = watchAreas
    .map((w) => {
      const n = w.name || "";
      if (n.includes("·"))
        return n.split("·")[1].replace(/\d{4}/, "").trim().toUpperCase();
      return n.replace(/Fire Brigade/i, "").trim().toUpperCase();
    })
    .filter(Boolean);
  const isWatched = (inc) =>
    watchSuburbs.some(
      (ws) =>
        (inc.locality || "").toUpperCase().includes(ws) ||
        (inc.address || "").toUpperCase().includes(ws)
    ) ||
    (inc.appliances || []).some((a) =>
      watchAreas.some((w) => w.code && a.callsign.startsWith(w.code))
    );
  const watchedIds = new Set(
    visible
      .filter((i) => !homeIds.has(i.id) && !areaIds.has(i.id) && isWatched(i))
      .map((i) => i.id)
  );
  matchRef.current = { inArea, isHome, isWatched };
  if (typeof window !== "undefined") window.__pfaProfile = profile;

  const q = feedSearch.trim().toUpperCase();
  const searched = q
    ? visible.filter((i) =>
        [i.type, i.address, i.locality, i.code, i.agency]
          .filter(Boolean)
          .some((v) => String(v).toUpperCase().includes(q))
      )
    : visible;
  const rank = (i) => (homeIds.has(i.id) ? 0 : watchedIds.has(i.id) ? 1 : 2);
  const sorted = [...searched].sort((a, b) => rank(a) - rank(b));

  const selected = incidents.find((i) => i.id === selectedId) || null;
  const cleared = Boolean(selectedId && loadedOnce && !selected);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/incidents");
      const list = r.data.incidents;
      const fresh = list.filter((i) => !seenRef.current.has(i.id));
      list.forEach((i) => seenRef.current.add(i.id));
      if (firstLoadDone.current && fresh.length) {
        const p = (r.data && window.__pfaProfile) || {};
        if (
          p.notify &&
          typeof Notification !== "undefined" &&
          Notification.permission === "granted"
        ) {
          const m = matchRef.current;
          fresh
            .filter((i) => m.inArea(i) || m.isHome(i) || m.isWatched(i))
            .forEach((i) => {
              new Notification(`${i.agency} — ${i.type}`, {
                body: [i.address, i.locality].filter(Boolean).join(", ") || "New incident",
                tag: i.id,
              });
            });
        }
      }
      firstLoadDone.current = true;
      setIncidents(list);
      setWarnings(r.data.warnings || []);
      setUpdatedAt(r.data.generated_at);
      setLoadedOnce(true);
    } catch (e) {}
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [load]);

  useEffect(() => {
    api.get("/stations/geo").then((r) => setStations(r.data.stations)).catch(() => {});
  }, []);

  return (
    <section
      id="demo"
      data-testid="live-demo-section"
      className="relative bg-[#0A0A0A] py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <div className="flex flex-wrap items-center gap-4">
            <p className="font-jb text-[11px] tracking-[0.35em] text-[#FF3B30]">
              01 / LIVE FEED
            </p>
            <span
              data-testid="live-feed-badge"
              className="border border-[#10B981]/40 bg-[#10B981]/10 px-3 py-1 font-jb text-[10px] tracking-[0.25em] text-[#10B981]"
            >
              LIVE — PAGER FEED
            </span>
          </div>
          <h2 className="mt-5 font-chivo text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
            The ground, right now.
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-zinc-400 md:text-base">
            Every incident below is live — FRV pager traffic via pocsag.info,
            CFA via the official VicEmergency feed. Refreshed every 15 seconds.
            No simulation. No delay theatre.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          data-testid="decoder-strip"
          className="no-scrollbar mt-12 flex gap-3 overflow-x-auto pb-2"
        >
          {DECODER.map(([code, name]) => (
            <div
              key={code}
              className="flex shrink-0 items-center gap-3 border border-[#27272A] bg-[#0D0D0D] px-4 py-2.5 transition-colors duration-300 hover:border-[#FF3B30]/50"
            >
              <span className="bg-[#FF3B30]/15 px-2 py-0.5 font-jb text-[11px] font-bold text-[#FF5247]">
                {code}
              </span>
              <span className="whitespace-nowrap font-jb text-[11px] text-zinc-400">
                {name}
              </span>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-8 flex flex-wrap items-center gap-2"
        >
          <div className="relative w-full md:w-80">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="incident-search-input"
              value={feedSearch}
              onChange={(e) => setFeedSearch(e.target.value)}
              placeholder="SEARCH AREA, STREET OR INCIDENT…"
              className="w-full border border-[#27272A] bg-[#0D0D0D] py-2.5 pl-11 pr-4 font-jb text-[11px] tracking-[0.15em] text-white placeholder:text-zinc-600 focus:border-[#FF3B30] focus:outline-none"
            />
          </div>
          {["ALL", "FRV", "CFA"].map((a) => (
            <button
              key={a}
              data-testid={`feed-filter-${a.toLowerCase()}`}
              onClick={() => setFeedFilter(a)}
              className={`border px-5 py-2.5 font-jb text-[11px] tracking-[0.25em] transition-colors duration-300 ${
                feedFilter === a
                  ? "border-[#FF3B30] bg-[#FF3B30]/15 text-[#FF5247]"
                  : "border-[#27272A] text-zinc-500 hover:border-zinc-500 hover:text-zinc-300"
              }`}
            >
              {a} · {a === "ALL" ? incidents.length : agencyCount(a)}
            </button>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mt-6 flex flex-wrap gap-2"
        >
          {[
            ["feed", "LIVE FEED"],
            ["stations", "FRV STATIONS"],
            ["appliances", "APPLIANCES"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`view-tab-${k}`}
              onClick={() => setViewMode(k)}
              className={`border px-5 py-2.5 font-jb text-[11px] tracking-[0.25em] transition-colors duration-300 ${
                viewMode === k
                  ? "border-[#FF3B30] bg-[#FF3B30] font-bold text-black"
                  : "border-[#27272A] text-zinc-500 hover:border-zinc-500 hover:text-zinc-300"
              }`}
            >
              {label}
            </button>
          ))}
        </motion.div>

        {viewMode === "feed" ? (
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3"
          >
            <div className="lg:col-span-2">
              <MapView
                incidents={sorted}
                stations={stations}
                warnings={warnings}
                panelOpen={Boolean(selectedId)}
              />
            </div>
            <IncidentFeed
              incidents={sorted}
              updatedAt={updatedAt}
              onSelect={setSelectedId}
              homeIds={homeIds}
              areaIds={areaIds}
              watchedIds={watchedIds}
            />
          </motion.div>
        ) : (
          <div className="mt-6">
            {viewMode === "stations" ? <StationsBoard /> : <ApplianceBoard />}
          </div>
        )}
        <IncidentDetail
          incident={selected}
          cleared={cleared}
          onClose={() => setSelectedId(null)}
          user={user}
        />
      </div>
    </section>
  );
}
